import React from 'react';
import { Alert, Button, Modal, Spin, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
  O365ApiCheck,
  O365VerifyResult,
} from '../../../../services/o365/o365Onboarding/o365Onboarding.model';
import {
  getOutcomeDisplay,
  getStatusDisplay,
  privacyDisplay,
  verifyDialogDescription,
} from '../o365ClientManagement.utils';

const { Text } = Typography;

export interface O365VerifyState {
  // The row's oauth_id this dialog was opened for. Lets the caller drop a response that resolves
  // after the dialog has moved on to a different row (see O365ClientManagement's handleVerify).
  oauthId: number;
  title: string;
  loading: boolean;
  result?: O365VerifyResult;
  error?: string;
}

const ERROR_TEXT = 'Verification failed unexpectedly - see the server log.';

const columns: ColumnsType<O365ApiCheck> = [
  { title: 'API', dataIndex: 'name', key: 'name' },
  {
    title: 'Result',
    key: 'outcome',
    render: (_, record) => {
      const display = getOutcomeDisplay(record.outcome);
      return <Tag color={display.color}>{display.label}</Tag>;
    },
  },
  {
    title: 'Permission',
    key: 'permission_hint',
    render: (_, record) =>
      record.permission_hint ? <Text code>{record.permission_hint}</Text> : '–',
  },
  {
    title: 'Details',
    key: 'reason',
    render: (_, record) => (
      <Text type="secondary" style={{ wordBreak: 'break-word' }}>
        {record.reason || ''}
      </Text>
    ),
  },
];

const VerifyResultModal: React.FC<{ state: O365VerifyState; onClose: () => void }> = ({
  state,
  onClose,
}) => {
  const { result } = state;
  const display = result ? getStatusDisplay(result.status) : null;
  const privacy = result?.privacy ? privacyDisplay[result.privacy.outcome] : undefined;

  return (
    <Modal
      wrapClassName="custom-modal"
      title={state.title}
      centered
      visible
      onCancel={onClose}
      footer={null}
      width={860}
    >
      <Spin spinning={state.loading}>
        {state.error && <Alert type="error" showIcon message={state.error} />}
        {result && display && (
          <>
            <div className="form-group">
              Status <Tag color={display.color}>{display.label}</Tag>
              {result.status === 'Error' ? (
                <div>
                  <Text type="danger">{ERROR_TEXT}</Text>
                </div>
              ) : (
                verifyDialogDescription[result.status] && (
                  <div>{verifyDialogDescription[result.status]}</div>
                )
              )}
              {result.error && result.error !== ERROR_TEXT && (
                <div>
                  <Text type="secondary" style={{ wordBreak: 'break-word' }}>
                    {result.error}
                  </Text>
                </div>
              )}
            </div>
            {result.warning && (
              <Alert
                style={{ marginBottom: 16 }}
                type="warning"
                showIcon
                message={result.warning}
              />
            )}
            {result.checks && result.checks.length > 0 && (
              <Table<O365ApiCheck>
                rowKey="api_id"
                size="small"
                columns={columns}
                dataSource={result.checks}
                pagination={false}
              />
            )}
            {privacy && (
              <Alert
                style={{ marginTop: 16 }}
                type={privacy.type}
                showIcon
                message={privacy.message}
                description={result.privacy?.fix}
              />
            )}
          </>
        )}
      </Spin>
      <div className="btns-block modal-footer">
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default VerifyResultModal;
